/**
 * ArchCurve — SVG pressure curve for an album's structure.
 *
 * Generic over track count; derives a smooth curve through synthesised
 * pressure values that map to the album's narrative arch:
 *   build → climax → drop at the hinge → drift down to closing.
 *
 * For now the pressure shape is canonical (any album with a hinge gets the
 * same up-then-down). When/if we get richer per-track data, this is the
 * single place to swap in real values without touching CSS.
 */

import { useMemo } from 'react';
import type { Track } from '@/types/album';

interface ArchCurveProps {
  tracks: Track[];
}

const W = 1000;
const H = 240;
const TOP_Y = 30;
const BASE_Y = H - 30;

function synthesisePressure(tracks: Track[]): number[] {
  const n = tracks.length;
  if (n === 0) return [];
  const hingeIdx = tracks.findIndex((t) => t.hinge);
  // If no explicit hinge, place it just past the centre.
  const h = hingeIdx >= 0 ? hingeIdx : Math.floor(n * 0.55);

  return tracks.map((_, i) => {
    if (i <= h) {
      // Rise from 0.18 → 0.92 across [0, h].
      const k = h === 0 ? 1 : i / h;
      return 0.18 + (0.92 - 0.18) * Math.pow(k, 0.95);
    }
    // Fall from 0.92 → 0.22 across (h, n-1].
    const k = (i - h) / Math.max(1, n - 1 - h);
    return 0.92 - (0.92 - 0.22) * Math.pow(k, 0.85);
  });
}

export function ArchCurve({ tracks }: ArchCurveProps): JSX.Element {
  const { d, pts, pressure, hinge, barW, range } = useMemo(() => {
    const pressure = synthesisePressure(tracks);
    const range = BASE_Y - TOP_Y;
    const barW = W / Math.max(1, pressure.length);
    const pts = pressure.map((p, i) => ({
      x: barW * (i + 0.5),
      y: BASE_Y - p * range,
    }));
    const d = pts
      .map((p, i) => {
        if (i === 0) return `M ${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
        const prev = pts[i - 1]!;
        const cx1 = prev.x + barW * 0.5;
        const cx2 = p.x - barW * 0.5;
        return `C ${cx1.toFixed(1)} ${prev.y.toFixed(1)}, ${cx2.toFixed(1)} ${p.y.toFixed(1)}, ${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
      })
      .join(' ');
    const hinge = tracks.findIndex((t) => t.hinge);
    return { d, pts, pressure, hinge, barW, range };
  }, [tracks]);

  return (
    <svg
      className="dd-arch-curve"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="archFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2D7FFF" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#2D7FFF" stopOpacity="0" />
        </linearGradient>
      </defs>

      <line x1="0" y1={BASE_Y} x2={W} y2={BASE_Y} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />

      {pressure.map((p, i) => {
        const x = barW * (i + 0.5);
        const y = BASE_Y - p * range;
        const isHinge = i === hinge;
        return (
          <line
            key={i}
            x1={x}
            x2={x}
            y1={BASE_Y}
            y2={y}
            stroke={isHinge ? 'rgba(184,147,58,0.55)' : 'rgba(255,255,255,0.10)'}
            strokeWidth={isHinge ? 1.5 : 1}
          />
        );
      })}

      {pressure.map((_, i) => (
        <text
          key={i}
          x={barW * (i + 0.5)}
          y={H - 10}
          fontSize="9"
          letterSpacing="0.1em"
          fill={i === hinge ? 'rgba(184,147,58,0.85)' : 'rgba(255,255,255,0.28)'}
          fontFamily="Barlow Condensed, sans-serif"
          fontWeight={i === hinge ? 600 : 500}
          textAnchor="middle"
        >
          {String(i + 1).padStart(2, '0')}
        </text>
      ))}

      <path d={`${d} L ${W} ${BASE_Y} L 0 ${BASE_Y} Z`} fill="url(#archFill)" />
      <path
        d={d}
        fill="none"
        stroke="rgba(45,127,255,0.85)"
        strokeWidth="1.4"
        strokeLinecap="round"
      />

      {pts.map((p, i) => (
        <circle
          key={i}
          cx={p.x}
          cy={p.y}
          r={i === hinge ? 3 : 1.8}
          fill={i === hinge ? '#B8933A' : 'rgba(45,127,255,0.9)'}
        />
      ))}
    </svg>
  );
}

/** Hinge x-fraction (0–1). Used by parent for hinge-tag positioning. */
export function hingeFraction(tracks: Track[]): number {
  const hingeIdx = tracks.findIndex((t) => t.hinge);
  const h = hingeIdx >= 0 ? hingeIdx : Math.floor(tracks.length * 0.55);
  return (h + 0.5) / Math.max(1, tracks.length);
}
